// @flow
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import moment from 'moment';
import { map, get, difference, includes, first, differenceWith } from 'lodash';
import type Moment from 'moment';
import {
  AgendaFilters, PlatformSwitch,
  AppMobileHeader,
  AppMobileFooter,
} from '@blocks';
import {
  Icon, Button, Calendar,
  CustomDayPicker, CalendarSwitcher, ProgressBar,
  AndroidAgenda,
} from '@elements';
import type {
  CompactAppointment,
  Clinic,
  Doctors,
  User,
  PermissionTo,
} from '@types';
import { sort, alphabeticCompare, useTranslationNs, getPlatform } from '@helpers/helpers';
import AppointmentService from '../../../services/AppointmentService';
import { getAgendaOwner } from './agendaOwner';
import '../../elements/Calendar/DoctorMark.scss';
import './Agenda.scss';

// The loading bar only shows once a load is slow (see the container): it starts with some green
const LOADING_BAR_START = 15;

type Props = {
  activeModal: ?string,
  activeClinic: Clinic,
  doctors: Doctors,
  user: User,
  appointments: Array<CompactAppointment>,
  onGetAppointments: Function,
  onShowAppointment: Function,
  onUpdateAppointment: Function,
  onNewAppointmentClick: Function,
  onChangeAgendaFilters: Function,
  onRefresh: Function,
  // Agenda Filters
  agendaFilters: Object,
  loadingItems: Object,
  isLoading: boolean,
  isRangeLoaded: boolean,
  rangeFailed: boolean,
  // Days not on screen yet are taking a while (see the container)
  showLoadingBar: boolean,
  // "Atualizar" was tapped and its request hasn't answered yet
  isRefreshing: boolean,
  highPrivacy: boolean,
  orderDoctorsByProfession: boolean,
  isFinatialEnabled: boolean,
  permissions: PermissionTo,
};


const Agenda = ({
  activeClinic, activeModal, agendaFilters, appointments, doctors, highPrivacy,
  isFinatialEnabled, isLoading, isRangeLoaded, isRefreshing, rangeFailed, loadingItems, onChangeAgendaFilters, onGetAppointments,
  onNewAppointmentClick, onRefresh, onShowAppointment, onUpdateAppointment, orderDoctorsByProfession, permissions, showLoadingBar, user }: Props) => {
  const [showMobileAgendaFilter, setShowMobileAgendaFilter] = useState(false);
  // The phone's day picker is this screen's own state: it never comes back open after leaving the agenda or relaunching
  const [isMobilePickerOpened, setMobilePickerOpened] = useState(false);
  const isAndroid = useMemo(() => getPlatform() === 'android', []);

  const {
    switchMode,
  } = agendaFilters;

  const [_, i18n] = useTranslationNs('agenda');

  const clinicDoctors = useMemo(() => {
    const clinicDoctorsTmp = {};
    map(get(activeClinic, ['doctorIds']), (doctorId) => {
      const doctor = get(doctors, ['data', doctorId]);
      clinicDoctorsTmp[doctorId] = doctor;
    });

    return clinicDoctorsTmp;
  }, [activeClinic, doctors]);

  useEffect(() => {
    window.lastSettingsScroll = 0;
  }, []);


  const selectedDate = useMemo(() => (agendaFilters.selectedDate ? moment(agendaFilters.selectedDate) : moment()), [agendaFilters.selectedDate]);
  const roomsFilter = useMemo(() => agendaFilters.roomsFilter || map(get(activeClinic, 'rooms', []), (room) => (room.number)), [activeClinic, agendaFilters.roomsFilter]);
  const allRoomsClinicIds = useMemo(() => map<any, any>(get(activeClinic, 'rooms', []), (room) => (room.number)), [activeClinic]);
  const isAllRoomsFilter = useMemo(() => differenceWith(allRoomsClinicIds, roomsFilter).length === 0, [allRoomsClinicIds, roomsFilter]);
  // const doctorsFilter = agendaFilters.doctorsFilter || (user ? [get(user, ['advancedData', 'doctorId'])] : []);
  const doctorsFilter = useMemo(() => get(agendaFilters, 'doctorsFilter', []), [agendaFilters]);
  const allDoctorClinicIds = useMemo(() => get(activeClinic, 'doctorIds', []), [activeClinic]);
  const isAllDoctorsFilter = useMemo(() => differenceWith(allDoctorClinicIds, doctorsFilter).length === 0, [allDoctorClinicIds, doctorsFilter]);
  // const firstId = doctors => Object.keys(doctors)[0] || null;
  // Filter appointments
  const filteredAppointments = useMemo(() => {
    const filteredAppointmentsTemp = [];
    map(appointments, (appointment) => {
      const { doctorId, room: roomId } = appointment;

      const doctorMatch = includes(doctorsFilter, doctorId) || isAllDoctorsFilter;
      const roomMatch = (roomId && includes(roomsFilter, roomId)) || isAllRoomsFilter;

      // check for matches with edge case - Blocked time have room defaulted to zero (0)
      if (doctorMatch && (roomMatch || roomId === 0)) {
        const highPrivacyBlocked = get(appointment, 'restricted', false) ? get(appointment, 'doctorId') !== get(user, ['advancedData', 'doctorId']) : false;

        filteredAppointmentsTemp.push({
          ...appointment,
          title: highPrivacyBlocked ? 'Consulta' : appointment.title,
        });
      }
    });
    return filteredAppointmentsTemp;
  }, [appointments, doctorsFilter, isAllDoctorsFilter, isAllRoomsFilter, roomsFilter, user]);

  const workHours = useMemo(() => {
    let workHoursTmp: any = [];
    if (doctorsFilter.length === 1) {
      const firstId = first(doctorsFilter);
      workHoursTmp = get(doctors, ['data', String(firstId), 'workHours']);
    }
    return workHoursTmp;
  }, [doctors, doctorsFilter]);

  const handleCancelDayPicker = useCallback(() => {
    setMobilePickerOpened(false);
  }, []);

  const allRoomsIds = useMemo(() => map<any, any>(activeClinic.rooms, (room) => (room.number)), [activeClinic.rooms]);

  const calendarFilter = useMemo(() => ({
    doctor: doctorsFilter,
    room: roomsFilter,
    showAllRooms: difference(allRoomsIds, roomsFilter).length === 0,
    showAllDoctors: difference(activeClinic.doctorIds, doctorsFilter).length === 0,
  }), [activeClinic.doctorIds, allRoomsIds, doctorsFilter, roomsFilter]);

  const orderedDoctors = useMemo(() => sort(clinicDoctors, alphabeticCompare('name')), [clinicDoctors]);

  const handleShowMobileFilter = useCallback(() => setShowMobileAgendaFilter(true), []);
  const handleHideMobileFilter = useCallback(() => setShowMobileAgendaFilter(false), []);

  const handleSetSelectedToCurrentDate = useCallback(() => {
    onChangeAgendaFilters({ selectedDate: moment() });
  }, [onChangeAgendaFilters]);

  const handlePrevDayClick = useCallback(() => {
    let prevDate = moment(agendaFilters.selectedDate).clone();
    prevDate = prevDate.isValid() ? prevDate.subtract(1, 'days') : moment().subtract(1, 'days');
    onChangeAgendaFilters({ selectedDate: prevDate });
  }, [agendaFilters.selectedDate, onChangeAgendaFilters]);

  const handleNextDayClick = useCallback(() => {
    let nextDate = moment(agendaFilters.selectedDate).clone();
    nextDate = nextDate.isValid() ? nextDate.add(1, 'days') : moment().add(1, 'days');
    onChangeAgendaFilters({ selectedDate: nextDate });

    // buggy code
    // const daysInCurrentMonth = selectedDate.daysInMonth();

    // let day;
    // let month;
    // let year;
    // if (selectedDate.date() === daysInCurrentMonth) {
    //   const isNextYear = (selectedDate.month() + 1) === 12;
    //   day = 1;
    //   month = isNextYear ? 0 : selectedDate.month() + 1;
    //   year = isNextYear ? selectedDate.year() + 1 : selectedDate.year();
    // } else {
    //   day = selectedDate.date() + 1;
    //   month = selectedDate.month();
    //   year = selectedDate.year();
    // }
  }, [onChangeAgendaFilters, agendaFilters.selectedDate]);

  const handleNextWeek = useCallback(() => {
    onChangeAgendaFilters({ selectedDate: moment(agendaFilters.selectedDate).clone().add(1, 'week') });
  }, [agendaFilters.selectedDate, onChangeAgendaFilters]);

  const handlePrevWeek = useCallback(() => {
    onChangeAgendaFilters({ selectedDate: moment(agendaFilters.selectedDate).clone().subtract(1, 'week') });
  }, [agendaFilters.selectedDate, onChangeAgendaFilters]);

  const handleNextMonth = useCallback(() => {
    onChangeAgendaFilters({ selectedDate: moment(agendaFilters.selectedDate).clone().add(1, 'month') });
  }, [agendaFilters.selectedDate, onChangeAgendaFilters]);

  const handlePrevMonth = useCallback(() => {
    onChangeAgendaFilters({ selectedDate: moment(agendaFilters.selectedDate).clone().subtract(1, 'month') });
  }, [agendaFilters.selectedDate, onChangeAgendaFilters]);

  const handlePickerMonthChange = useCallback((date: string) => {
    const parsedDate = moment(date);
    selectedDate.month(parsedDate.month());
    selectedDate.year(parsedDate.year());
    onChangeAgendaFilters({ selectedDate });
  }, [onChangeAgendaFilters, selectedDate]);

  const handleSelectDay = useCallback((date: string) => {
    onChangeAgendaFilters({ selectedDate: moment(date) });
  }, [onChangeAgendaFilters]);

  const handleModeChange = useCallback((newSwitchMode: string) => {
    onChangeAgendaFilters({ switchMode: newSwitchMode });
  }, [onChangeAgendaFilters]);

  const toggleMobileDayPicker = useCallback(() => {
    setMobilePickerOpened((opened) => !opened);
  }, []);

  const handleAcceptDayPicker = useCallback(() => {
    setMobilePickerOpened(false);
    return selectedDate;
  }, [selectedDate]);

  const handleFiltersChange = useCallback((selectedDoctors: Array<number>, selectedRooms: Array<number>) => {
    if (selectedRooms && selectedDoctors) {
      onChangeAgendaFilters({
        roomsFilter: selectedRooms,
        doctorsFilter: selectedDoctors,
      });
    } else if (selectedRooms && !selectedDoctors) {
      onChangeAgendaFilters({
        roomsFilter: selectedRooms,
      });
    } else if (!selectedRooms && selectedDoctors) {
      onChangeAgendaFilters({
        doctorsFilter: selectedDoctors,
      });
    }
    handleHideMobileFilter();
  }, [handleHideMobileFilter, onChangeAgendaFilters]);

  // The refresh icon reloads what is on screen: on Android that is a day, a week or a month
  const handleRefreshClick = useCallback(() => {
    if (isAndroid) onRefresh(AppointmentService.singleDayParam(switchMode || 'day'));
    else onRefresh();
  }, [isAndroid, onRefresh, switchMode]);

  const handleNewAppointmentClick = useCallback((start: Moment, end: Moment, isDayClick: boolean) => {
    onNewAppointmentClick(doctorsFilter, start, end, isDayClick);
  }, [doctorsFilter, onNewAppointmentClick]);

  const handleRefresh = useCallback(() => {
    onRefresh(null);
  }, [onRefresh]);

  // Whose agenda is on the phone's screen: its header has no room to say it. Not memoized: `_` is new on every render
  const agendaOwner = getAgendaOwner({
    doctors: orderedDoctors,
    rooms: get(activeClinic, 'rooms', []),
    permissions,
    doctorsFilter,
    roomsFilter,
    isAllDoctors: isAllDoctorsFilter,
    isAllRooms: isAllRoomsFilter,
    _,
  });

  // Android's week and month lists show placeholders on the days still loading: no bar on top of them
  const showMobileLoadingBar = showLoadingBar && !(isAndroid && (switchMode === 'week' || switchMode === 'month'));

  return (
    <PlatformSwitch
      inject
      desktop={() => (
        <div id="agendaPage" className="appContent">
          <ProgressBar show={showLoadingBar} startAt={LOADING_BAR_START} />
          <aside className="sidebar">
            <div className="sidebarActions">
              <Button className="primary" text={_('label/new_appointment')} onClick={handleNewAppointmentClick} />
            </div>
            <div className="datepicker">
              <CalendarSwitcher
                selectedDate={selectedDate}
                onChangeMode={handleModeChange}
                onNextWeek={handleNextWeek}
                onPrevWeek={handlePrevWeek}
                onPrevDayClick={handlePrevDayClick}
                onNextDayClick={handleNextDayClick}
                onSetSelectedToCurrentDate={handleSetSelectedToCurrentDate}
                switchCalendarBy={switchMode === 'week' ? 'week' : 'day'}
              />

              <div className="dayPicker">
                <CustomDayPicker
                  disableSaturday={!get(activeClinic, 'showSaturday', false) && switchMode === 'week'}
                  disableSunday={!get(activeClinic, 'showSunday', false) && switchMode === 'week'}
                  highlightWeek={switchMode === 'week'}
                  advancedMode
                  selectedDate={selectedDate}
                  onMonthChange={handlePickerMonthChange}
                  onSelectDay={handleSelectDay}
                />
              </div>

              <Button
                className="linkButton agendaRefreshButton"
                onClick={handleRefresh}
              >
                <Icon iconName="refresh" className="agendaRefreshIcon" size={14} spin={isRefreshing} />
                {_('action/refresh')}
              </Button>
            </div>
            <AgendaFilters
              rooms={sort(activeClinic.rooms, alphabeticCompare('name'))}
              doctors={orderedDoctors}
              defaultRooms={roomsFilter}
              defaultDoctorId={doctorsFilter}
              onFiltersChange={handleFiltersChange}
              highPrivacy={highPrivacy}
              user={user}
              orderDoctorsByProfession={orderDoctorsByProfession}
              permissions={permissions}
            />
          </aside>
          <section className="mainSection">
            <Calendar
              loadingItems={loadingItems}
              isLoading={isLoading}
              activeModal={activeModal}
              clinic={activeClinic}
              clinicDoctors={clinicDoctors}
              calendarView={switchMode === 'week' ? 'timeGridWeek' : 'timeGridDay'}
              selectedDate={selectedDate.format('YYYY-MM-DD')}
              appointments={filteredAppointments}
              onGetAppointments={onGetAppointments}
              onShowAppointment={onShowAppointment}
              onUpdateAppointment={onUpdateAppointment}
              onNewAppointmentClick={handleNewAppointmentClick}
              user={user}
              webView
              filter={calendarFilter}
              workHours={workHours}
              isFinatialEnabled={isFinatialEnabled}
              language={get(i18n, 'language')}
            />
          </section>
        </div>
      )}
      mobile={() => (
        <React.Fragment>
          {!showMobileAgendaFilter &&
          <AppMobileHeader hideBack disableHeader>
            <CalendarSwitcher
              isMobile
              isAndroid={isAndroid}
              onToggleMobileDayPicker={toggleMobileDayPicker}
              selectedDate={selectedDate}
              onChangeMode={handleModeChange}
              onNextWeek={handleNextWeek}
              onPrevWeek={handlePrevWeek}
              onNextMonth={handleNextMonth}
              onPrevMonth={handlePrevMonth}
              onPrevDayClick={handlePrevDayClick}
              onNextDayClick={handleNextDayClick}
              onSetSelectedToCurrentDate={handleSetSelectedToCurrentDate}
              onMobileFilterClick={handleShowMobileFilter}
              switchCalendarBy={isAndroid ? (switchMode || 'day') : 'day'}
              mobileGreenFilter={!calendarFilter.showAllDoctors || !calendarFilter.showAllRooms}
            />
            <Icon iconName="add" className="icon" onClick={handleNewAppointmentClick} />
            <Icon iconName="refresh" className="icon agendaRefreshIcon" spin={isRefreshing} onClick={handleRefreshClick} />
          </AppMobileHeader>
            }
          { !showMobileAgendaFilter &&
          <div className="mobileProgressbarHolder"><ProgressBar show={showMobileLoadingBar} startAt={LOADING_BAR_START} /></div>
            }
          {showMobileAgendaFilter &&
          <AppMobileHeader title="Filtros" onBackClick={handleHideMobileFilter} />
            }
          <div id="agendaPage" className="appContent">
            {!showMobileAgendaFilter && agendaOwner && (
              <button type="button" className="agendaOwner" onClick={handleShowMobileFilter}>
                {agendaOwner.mark === 'room' ? (
                  <Icon iconName="location" className="agendaOwnerRoom" size={16} aria-hidden="true" />
                ) : (
                  <span className={`agendaDoctorMark ${agendaOwner.mark}`} aria-hidden="true" />
                )}
                <span className="agendaOwnerText">{agendaOwner.text}</span>
                <Icon iconName="arrow-down" className="agendaOwnerArrow" size={18} aria-hidden="true" />
              </button>
            )}
            {!showMobileAgendaFilter && isAndroid &&
              <AndroidAgenda
                activeModal={activeModal}
                loadingItems={loadingItems}
                isLoading={isLoading}
                isRangeLoaded={isRangeLoaded}
                rangeFailed={rangeFailed}
                clinic={activeClinic}
                clinicDoctors={clinicDoctors}
                switchMode={switchMode || 'day'}
                selectedDate={selectedDate}
                appointments={filteredAppointments}
                onShowAppointment={onShowAppointment}
                onNewAppointmentClick={handleNewAppointmentClick}
                onGetAppointments={onGetAppointments}
                onSelectDay={handleSelectDay}
                onChangeMode={handleModeChange}
                user={user}
                filter={calendarFilter}
                workHours={workHours}
                language={get(i18n, 'language')}
                isFinatialEnabled={isFinatialEnabled}
              />
                }
            {!showMobileAgendaFilter && !isAndroid &&
              <Calendar
                loadingItems={loadingItems}
                isLoading={isLoading}
                activeModal={activeModal}
                clinic={activeClinic}
                clinicDoctors={clinicDoctors}
                calendarView="timeGridDay"
                selectedDate={selectedDate.format('YYYY-MM-DD')}
                appointments={filteredAppointments}
                onGetAppointments={onGetAppointments}
                onShowAppointment={onShowAppointment}
                onUpdateAppointment={onUpdateAppointment}
                onNewAppointmentClick={handleNewAppointmentClick}
                user={user}
                filter={calendarFilter}
                workHours={workHours}
                isFinatialEnabled={isFinatialEnabled}
                language={get(i18n, 'language')}
              />
                }
            {showMobileAgendaFilter &&
              <AgendaFilters
                rooms={sort(activeClinic.rooms, alphabeticCompare('name'))}
                doctors={orderedDoctors}
                defaultRooms={roomsFilter}
                defaultDoctorId={doctorsFilter}
                onFiltersChange={handleFiltersChange}
                highPrivacy={highPrivacy}
                user={user}
                orderDoctorsByProfession={orderDoctorsByProfession}
                permissions={permissions}
              />
                }

            <CustomDayPicker
              disableSaturday={!get(activeClinic, 'showSaturday', false)}
              disableSunday={!get(activeClinic, 'showSunday', false)}
              acceptOnSelect
              withOverlay
              showButtons
              advancedMode
              isOpen={isMobilePickerOpened}
              selectedDate={selectedDate}
              onMonthChange={handlePickerMonthChange}
              onSelectDay={handleSelectDay}
              onCancel={handleCancelDayPicker}
              onAccept={handleAcceptDayPicker}
              showToday
            />

          </div>
          {!showMobileAgendaFilter && <AppMobileFooter />}
        </React.Fragment>
      )}
    />
  );
};

// $FlowFixMe
export default React.memo(Agenda);
